import { computed, ref, unref, watch } from 'vue';
import {
  autoUpdate,
  computePosition,
  Placement,
  Strategy,
  offset,
  autoPlacement,
  flip,
  shift,
  hide,
  arrow,
  size,
  Padding,
} from '@floating-ui/dom';
import { MaybeElementRef, MaybeRef, unrefElement, VueInstance } from '@vueuse/core';

export type UseFloatingUiOptions = {
  placement?: Placement;
  strategy?: Strategy;
  offset?: number;
  autoPlacement?: boolean;
  flip?: boolean;
  shift?: boolean;
  hide?: boolean;
  arrow?: boolean;
  fullWidth?: boolean;
  allowModalOverflow?: boolean;
  modalOverflowPadding?: Padding;
};

export const DEFAULT_OPTIONS: UseFloatingUiOptions = {
  placement: 'bottom-start',
  offset: 8,
  autoPlacement: false,
  flip: true,
  shift: false,
  hide: false,
  arrow: true,
  fullWidth: false,
  allowModalOverflow: false,
  modalOverflowPadding: {
    top: 8,
    left: 8,
    right: 8,
    bottom: 32,
  },
};

export const useFloatingUi = (
  reference: MaybeElementRef<HTMLElement | VueInstance | undefined>,
  floating: MaybeElementRef<HTMLElement | VueInstance | undefined>,
  options: MaybeRef<UseFloatingUiOptions> = DEFAULT_OPTIONS,
  arrowRef: MaybeElementRef<HTMLElement | VueInstance | undefined> = undefined
) => {
  let destroy: () => void | undefined;
  const hasMounted = ref<boolean>(false);

  const referenceEl = computed(() => unrefElement(reference) as Element | undefined);
  const floatingEl = computed(() => unrefElement(floating) as HTMLElement | undefined);

  const arrowEl = computed(() => unrefElement(arrowRef) as HTMLElement | undefined);

  watch(
    [referenceEl, floatingEl],
    () => {
      if (!hasMounted.value && referenceEl.value && floatingEl.value) {
        destroy = autoUpdate(referenceEl.value, floatingEl.value, () => {
          let arrowLen = 0;
          if (arrowEl.value) {
            arrowLen = arrowEl.value.offsetWidth;
          }

          if (referenceEl.value && floatingEl.value) {
            const _options = {
              ...DEFAULT_OPTIONS,
              ...unref(options),
            };

            const middleware = [];

            if (_options.offset) {
              middleware.push(offset(_options.offset));
            }

            if (_options.autoPlacement) {
              middleware.push(autoPlacement());
            }

            if (_options.flip && !_options.autoPlacement) {
              middleware.push(flip());
            }

            if (_options.shift) {
              middleware.push(shift());
            }

            if (_options.hide) {
              middleware.push(hide());
            }

            if (_options.arrow && arrowEl.value) {
              middleware.push(arrow({ element: arrowEl.value }));
            }

            if (_options.fullWidth) {
              middleware.push(
                size({
                  apply({ rects }) {
                    if (!floatingEl.value) return;
                    Object.assign(floatingEl.value.style, {
                      width: `${rects.reference.width}px`,
                    });
                  },
                })
              );
            }

            // Ensures that the floating element isn’t too big to fit in the viewport (or more specifically, its clipping context),
            // especially when a maximum size isn’t specified.
            if (!_options.allowModalOverflow) {
              middleware.push(
                size({
                  // The padding is used to prevent the floating element from being too close to the clipping contexts edges.
                  padding: _options.modalOverflowPadding,
                  apply({ availableHeight, availableWidth }) {
                    if (!floatingEl.value) return;
                    Object.assign(floatingEl.value.style, {
                      maxHeight: `${availableHeight}px`,
                      maxWidth: `${availableWidth}px`,
                    });
                  },
                })
              );
            }

            computePosition(referenceEl.value, floatingEl.value, {
              placement: _options.placement,
              strategy: _options.strategy,
              middleware,
            }).then(({ x, y, placement, middlewareData }) => {
              if (floatingEl.value) {
                const transformOrigins: Record<Placement, string> = {
                  bottom: 'top',
                  'bottom-end': 'top right',
                  'bottom-start': 'top left',
                  left: 'right',
                  'left-end': 'right bottom',
                  'left-start': 'right top',
                  right: 'left',
                  'right-end': 'left bottom',
                  'right-start': 'left top',
                  top: 'bottom',
                  'top-end': 'bottom right',
                  'top-start': 'bottom left',
                };

                // Move floating element's position
                Object.assign(floatingEl.value.style, {
                  left: `${x}px`,
                  top: `${y}px`,
                  transformOrigin: transformOrigins[placement],
                });

                if (_options.hide) {
                  const { referenceHidden } = middlewareData.hide!;

                  // Hide element
                  Object.assign(floatingEl.value.style, {
                    visibility: referenceHidden ? 'hidden' : 'visible',
                  });
                }

                const side = placement.split('-')[0];

                const staticSide = {
                  top: 'bottom',
                  right: 'left',
                  bottom: 'top',
                  left: 'right',
                }[side];

                if (middlewareData.arrow && arrowEl.value) {
                  const { x, y } = middlewareData.arrow;
                  Object.assign(arrowEl.value.style, {
                    left: x != null ? `${x}px` : '',
                    top: y != null ? `${y}px` : '',
                    // Ensure the static side gets unset when
                    // flipping to other placements' axes.
                    right: '',
                    bottom: '',
                    [staticSide!]: `${-arrowLen / 2}px`,
                  });
                }
              }
            });
          }
        });

        hasMounted.value = true;
      } else if (hasMounted.value && (!referenceEl.value || !floatingEl.value)) {
        destroy();
        hasMounted.value = false;
      }
    },
    { immediate: true }
  );
};
